import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Injectable, inject } from '@angular/core';
import {
  UseCaseInputField,
  UseCaseInputGroup,
  UseCaseInputType,
} from '../models/use-case.models';

import { FormProperty } from '../models/form.models';
import { NomenclatureService } from './nomenclature.service';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

/**
 * A tag_list field carrying an itemShape (§4.2/§15) - rendered by the structured
 * repeater (UseCaseItemListComponent), not by `<app-dynamic-form>`.
 */
export interface UseCaseRepeaterField {
  key: string;
  label: string;
  itemShape: Record<string, UseCaseInputType>;
  validators: ValidatorFn[];
  defaultValue: unknown[];
}

/**
 * A source_list field (§4.2) - rendered by the server-filtered nomenclature
 * picker (UseCaseSourcePickerComponent), not by `<app-dynamic-form>`.
 */
export interface UseCaseSourcePickerField {
  key: string;
  label: string;
  nomenclature: string;
  validators: ValidatorFn[];
  defaultValue: number[];
}

// Push service delivery methods for push_service_list. The backend template
// default is the string alias `["email"]` (SPEC §15), so the checkbox values
// are the aliases, not numeric nomenclature ids.
const PUSH_SERVICE_OPTIONS = [
  { value: 'one_signal', labelKey: 'use-case.input.channel.mobile-push' },
  { value: 'email', labelKey: 'use-case.input.channel.email' },
  { value: 'mattermost', labelKey: 'use-case.input.channel.mattermost' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The single interpretation point for §4.2 input-schema field types. Maps a
 * template's input schema (for one wizard step group) to FormProperty[] for
 * `<app-dynamic-form>`. Keep the type mapping in this one place (§4.2, §13):
 * a new field type is one row here plus backend validator support.
 */
@Injectable({ providedIn: 'root' })
export class UseCaseFormMapperService {
  private readonly translate = inject(TranslateService);
  private readonly nomenclature = inject(NomenclatureService);

  /**
   * Build the FormProperty list for the fields of one group (§4.3). Structured
   * repeater fields (tag_list + itemShape) are excluded - they are returned by
   * getRepeaterFields() and rendered by UseCaseItemListComponent instead.
   *
   * @param fields the template input schema
   * @param group which wizard step's fields to emit ('business' | 'preferences')
   * @returns the FormProperty list for `<app-dynamic-form>`
   */
  mapFields(
    fields: UseCaseInputField[],
    group: UseCaseInputGroup,
  ): FormProperty[] {
    const groupFields = this.fieldsForGroup(fields, group).filter(
      (field) => !this.isRepeater(field) && field.type !== 'source_list',
    );
    // Keys referenced by any `when` in this group must re-trigger validation
    // and visibility when their value changes (§3.2).
    const referencedKeys = this.collectWhenReferencedKeys(groupFields);

    return groupFields.map((field) =>
      this.mapField(field, referencedKeys.has(field.key)),
    );
  }

  /**
   * The structured-repeater fields for one group: tag_list fields carrying an
   * itemShape (§4.2). Each renders as UseCaseItemListComponent.
   *
   * @param fields the template input schema
   * @param group which wizard step's fields to emit
   * @returns the repeater field descriptors
   */
  getRepeaterFields(
    fields: UseCaseInputField[],
    group: UseCaseInputGroup,
  ): UseCaseRepeaterField[] {
    return this.fieldsForGroup(fields, group)
      .filter((field) => this.isRepeater(field))
      .map((field) => ({
        key: field.key,
        label: this.label(field),
        itemShape: field.itemShape ?? {},
        validators: this.buildValidators(field),
        defaultValue: Array.isArray(field.default) ? field.default : [],
      }));
  }

  /**
   * The source-picker fields for one group: source_list fields (§4.2), each
   * rendered by UseCaseSourcePickerComponent over the source nomenclature.
   *
   * @param fields the template input schema
   * @param group which wizard step's fields to emit
   * @returns the source-picker field descriptors
   */
  getSourcePickerFields(
    fields: UseCaseInputField[],
    group: UseCaseInputGroup,
  ): UseCaseSourcePickerField[] {
    return this.fieldsForGroup(fields, group)
      .filter((field) => field.type === 'source_list')
      .map((field) => ({
        key: field.key,
        label: this.label(field),
        nomenclature: 'source',
        validators: this.buildValidators(field),
        defaultValue: Array.isArray(field.default)
          ? (field.default as number[])
          : [],
      }));
  }

  /**
   * Map an itemShape to the per-row FormProperty list (used by the repeater).
   * Reuses the same §4.2 type mapping as top-level fields, so a row's `name`
   * renders as text and `keywords` as the real tag-input control.
   *
   * @param itemShape the itemShape key -> type map
   * @returns the row FormProperty list
   */
  mapItemShapeToProperties(
    itemShape: Record<string, UseCaseInputType>,
  ): FormProperty[] {
    return Object.entries(itemShape).map(([key, type]) => {
      const property: FormProperty = {
        propertyName: key,
        propertyLabel: this.translate.instant(`use-case.input.item.${key}`),
        controlType: this.controlType(type),
      };
      if (this.isListType(type)) {
        property.defaultValue = [];
      }
      if (property.controlType === 'tag-input') {
        property.additionalConfig = this.freeEntryTagConfig();
      }

      return property;
    });
  }

  /**
   * Fields belonging to a group (§4.3); ungrouped defaults to business.
   *
   * @param fields the template input schema
   * @param group the target group
   * @returns the group's fields
   */
  private fieldsForGroup(
    fields: UseCaseInputField[],
    group: UseCaseInputGroup,
  ): UseCaseInputField[] {
    return fields.filter((field) => (field.group ?? 'business') === group);
  }

  /**
   * Whether a field is a structured repeater (tag_list + itemShape).
   *
   * @param field the input field
   * @returns true when the field needs the repeater
   */
  private isRepeater(field: UseCaseInputField): boolean {
    return (
      field.type === 'tag_list' &&
      !!field.itemShape &&
      Object.keys(field.itemShape).length > 0
    );
  }

  /**
   * Map one input field to a FormProperty.
   *
   * @param field the input field definition
   * @param isWhenTarget whether another field's `when` references this field
   * @returns the FormProperty
   */
  private mapField(
    field: UseCaseInputField,
    isWhenTarget: boolean,
  ): FormProperty {
    const property: FormProperty = {
      propertyName: field.key,
      propertyLabel: this.label(field),
      controlType: this.controlType(field.type),
      formChanger: isWhenTarget || undefined,
    };

    this.applyDefault(field, property);
    this.applyControlConfig(field, property);
    this.applyConditional(field, property);

    return property;
  }

  /**
   * §4.2 type -> FormProperty.controlType. country_list and language_list are
   * multi-select `autocomplete`s over their nomenclatures (bounded sets,
   * load-all via NomenclatureService.fetchCountries / fetchLanguages).
   * source_list is NOT rendered via a
   * FormProperty (it is excluded from mapFields) - it renders through the
   * server-filtered UseCaseSourcePickerComponent (getSourcePickerFields); its
   * entry below is an inert fallback.
   *
   * @param type the schema field type
   * @returns the FormProperty controlType
   */
  private controlType(type: UseCaseInputType): FormProperty['controlType'] {
    const map: Record<UseCaseInputType, FormProperty['controlType']> = {
      text: 'text',
      number: 'input-number',
      toggle: 'toggle',
      select: 'select',
      tag_list: 'tag-input',
      email_list: 'tag-input',
      language_list: 'autocomplete',
      country_list: 'autocomplete',
      source_list: 'tag-input',
      push_service_list: 'checkbox-array',
      principal_list: 'principal-picker',
    };

    return map[type];
  }

  /**
   * Seed defaultValue from the schema `default`, with control-appropriate
   * fallbacks for list/toggle controls.
   *
   * @param field the input field
   * @param property the FormProperty being built (mutated)
   * @return void
   */
  private applyDefault(field: UseCaseInputField, property: FormProperty): void {
    if (field.default !== undefined) {
      property.defaultValue = field.default;
    } else if (field.type === 'toggle') {
      property.defaultValue = false;
    } else if (this.isListType(field.type)) {
      property.defaultValue = [];
    }
  }

  /**
   * Attach control-specific config (select options, push-service checkboxes)
   * and static validators derived from `required`/`rules`.
   *
   * @param field the input field
   * @param property the FormProperty being built (mutated)
   * @return void
   */
  private applyControlConfig(
    field: UseCaseInputField,
    property: FormProperty,
  ): void {
    if (property.controlType === 'tag-input') {
      property.additionalConfig = this.freeEntryTagConfig();
    }

    if (field.type === 'select') {
      property.valueOptions = (field.options ?? []).map((option) => ({
        value: option,
        name: this.translate.instant(`use-case.input.option.${option}`),
      }));
    }

    if (field.type === 'country_list') {
      // Reuse the existing countries nomenclature source (bounded set) as the
      // async option list for the multi-select autocomplete. /utils/countries
      // items are location-shaped: location_id / location_name.
      property.valueOptionsAsync = this.nomenclature.fetchCountries().pipe(
        map((countries) =>
          [...countries]
            .sort((a, b) => a.location_name.localeCompare(b.location_name))
            .map((country) => ({
              value: country.location_id,
              name: country.location_name,
            })),
        ),
      );
    }

    if (field.type === 'language_list') {
      property.valueOptionsAsync = this.nomenclature.fetchLanguages().pipe(
        map((languages) =>
          [...languages]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((language) => ({
              value: language.id,
              name: language.name,
            })),
        ),
      );
    }

    if (field.type === 'push_service_list') {
      property.additionalConfig = {
        values: PUSH_SERVICE_OPTIONS.map((option) => ({
          value: option.value,
          name: this.translate.instant(option.labelKey),
        })),
      };
    }

    // Fields without a `when` get static validators. Conditional fields get
    // theirs through validateBy so a hidden field is not required (§5.2).
    if (!field.when) {
      const validators = this.buildValidators(field);
      if (validators.length > 0) {
        property.validators = validators;
      }
    }
  }

  /**
   * additionalConfig for a free-entry tag-input (tag_list without itemShape,
   * email_list): no suggestion source, and typed terms are kept as plain
   * strings in the control value - the shape the backend InputValidator and
   * the email-list validator expect.
   *
   * @returns the tag-input additionalConfig
   */
  private freeEntryTagConfig(): Record<string, unknown> {
    return {
      valueOptions: [],
      displayBy: 'name',
      identifyBy: 'id',
      addTag: (term: string) => ({ id: term, name: term }),
    };
  }

  /**
   * Wire `when` (§3.2) to visibility and conditional validation: hidden fields
   * are neither shown nor required, matching the backend's conditional
   * requiredness (§5.2).
   *
   * @param field the input field
   * @param property the FormProperty being built (mutated)
   * @return void
   */
  private applyConditional(
    field: UseCaseInputField,
    property: FormProperty,
  ): void {
    if (!field.when) {
      return;
    }

    const predicate = this.whenPredicate(field.when);
    property.shouldDisplay = predicate;
    property.validateBy = (formGroup: FormGroup) =>
      predicate(formGroup) ? this.buildValidators(field) : [];
  }

  /**
   * Compile a §3.2 `when` expression (`inputs.<key>` or `!inputs.<key>`) to a
   * truthiness predicate over the step's FormGroup. A referenced control that
   * lives in another step's group is treated as visible (the wizard cannot see
   * cross-group values; the backend re-validates authoritatively).
   *
   * @param when the `when` expression
   * @returns a predicate over the FormGroup
   */
  private whenPredicate(when: string): (formGroup: FormGroup) => boolean {
    const negated = when.trim().startsWith('!');
    const key = when
      .trim()
      .replace(/^!/, '')
      .replace(/^inputs\./, '');

    return (formGroup: FormGroup) => {
      const control = formGroup?.get(key);
      let outcome: boolean;
      if (!control) {
        outcome = true;
      } else {
        const truthy = this.isTruthy(control.value);
        outcome = negated ? !truthy : truthy;
      }

      return outcome;
    };
  }

  /**
   * Build the validator list for a field from `required` and `rules`.
   *
   * @param field the input field
   * @returns the validators
   */
  private buildValidators(field: UseCaseInputField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    const rules = field.rules ?? {};
    const isList = this.isListType(field.type);

    if (field.required) {
      validators.push(isList ? this.minItemsValidator(1) : Validators.required);
    }
    if (rules.minItems !== undefined) {
      validators.push(this.minItemsValidator(rules.minItems));
    }
    if (rules.maxItems !== undefined) {
      validators.push(this.maxItemsValidator(rules.maxItems));
    }
    if (rules.maxLength !== undefined) {
      validators.push(Validators.maxLength(rules.maxLength));
    }
    if (rules.pattern !== undefined) {
      validators.push(Validators.pattern(rules.pattern));
    }
    if (field.type === 'email_list') {
      validators.push(this.emailListValidator());
    }

    return validators;
  }

  /**
   * Collect the input keys referenced by any `when` in a set of fields.
   *
   * @param fields the fields to scan
   * @returns the set of referenced keys
   */
  private collectWhenReferencedKeys(fields: UseCaseInputField[]): Set<string> {
    const keys = new Set<string>();
    fields.forEach((field) => {
      if (field.when) {
        keys.add(
          field.when
            .trim()
            .replace(/^!/, '')
            .replace(/^inputs\./, ''),
        );
      }
    });

    return keys;
  }

  /**
   * Resolve a field's label to its translate key (label_i18n) or a fallback
   * derived from the key.
   *
   * @param field the input field
   * @returns the resolved label
   */
  private label(field: UseCaseInputField): string {
    const key = field.label_i18n ?? `use-case.input.${field.key}`;

    return this.translate.instant(key);
  }

  /**
   * Whether a schema type stores an array value.
   *
   * @param type the schema field type
   * @returns true for list-shaped types
   */
  private isListType(type: UseCaseInputType): boolean {
    return [
      'tag_list',
      'email_list',
      'language_list',
      'country_list',
      'source_list',
      'push_service_list',
      'principal_list',
    ].includes(type);
  }

  /**
   * Truthiness per §3.2: empty array/string/null are falsy.
   *
   * @param value the control value
   * @returns whether the value is truthy
   */
  private isTruthy(value: unknown): boolean {
    let outcome: boolean;
    if (Array.isArray(value)) {
      outcome = value.length > 0;
    } else {
      outcome = !!value;
    }

    return outcome;
  }

  /**
   * Validator: the array control holds at least `min` items.
   *
   * @param min minimum item count
   * @returns the validator
   */
  private minItemsValidator(min: number): ValidatorFn {
    return (control) => {
      const value = control.value;
      const length = Array.isArray(value) ? value.length : 0;

      return length >= min
        ? null
        : { minItems: { required: min, actual: length } };
    };
  }

  /**
   * Validator: the array control holds at most `max` items.
   *
   * @param max maximum item count
   * @returns the validator
   */
  private maxItemsValidator(max: number): ValidatorFn {
    return (control) => {
      const value = control.value;
      const length = Array.isArray(value) ? value.length : 0;

      return length <= max
        ? null
        : { maxItems: { allowed: max, actual: length } };
    };
  }

  /**
   * Validator: every item in the array control is a valid email address.
   *
   * @returns the validator
   */
  private emailListValidator(): ValidatorFn {
    return (control) => {
      const value = control.value;
      const invalid =
        Array.isArray(value) &&
        value.some((item) => !EMAIL_PATTERN.test(String(item)));

      return invalid ? { emailList: true } : null;
    };
  }
}
